import {css, StyleSheet} from 'aphrodite';
import React, {Dispatch, useCallback, useEffect, useMemo, useReducer, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import CSVFilePreview from 'external-files/CSVFilePreview';
import {ConnectionStatus, InstrumentServiceDetails} from 'instruments/connections/ConnectionConstants';
import {getInstrumentServiceDetails} from 'instruments/connections/ConnectionDAO';
import {AutomationOutputFileConfig} from 'lab-automation/automation-files/LabAutomationConstants';
import {ClientAutomationOutputFileConfigSpec} from 'lab-automation/settings/RunSchemaSpecConstants';
import notifications from 'notifications';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  LAB_AUTOMATION_RETRIEVE_CONNECT_FILE_STARTED_EVENT,
  LAB_AUTOMATION_RETRIEVE_CONNECT_FILE_ENDED_EVENT,
  LAB_AUTOMATION_CREATE_CONNECT_FILE_STARTED_EVENT,
  LAB_AUTOMATION_CREATE_CONNECT_FILE_ENDED_EVENT,
} from 'performance/processLabAutomationEvents';
import {FilterKey} from 'search/SearchConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import StepNav, {StepNavVariant} from 'ui-library/StepNav';
import * as DateUtil from 'util/DateUtil';

import reducer, {
  ImportInstrumentDataModalAction,
  ImportInstrumentDataModalActionType,
} from './importInstrumentDataModalReducer';
import {
  ImportInstrumentDataModalState,
  InstrumentDataFileType,
  InstrumentData,
  InstrumentQueryStatus,
  isUnselectableFileStatus,
  StepKey,
  INITIAL_STATE,
  InstrumentFileStatus,
} from './InstrumentDataConstants';
import {
  createCopyObjectInstrumentQuery,
  pollInstrumentQueryStatus,
  searchInstrumentData,
} from './InstrumentDataDAO';
import InstrumentDataTable from './InstrumentDataTable';
import InstrumentLoginPage from './InstrumentLoginPage';

export const MISSING_TRANSFORM_TYPE_ERROR =
  "Invalid run schema - must set 'Instrument data type' type on all output file configurations. Please fix run schema and retry.";

export interface ImportInstrumentDataModalProps {
  allowMultiple: boolean;
  automationOutputFileConfig: AutomationOutputFileConfig | ClientAutomationOutputFileConfigSpec;
  connectionSchemaId?: string;
  connectionId?: string;
  runId?: string;
  shouldShowUploadedFilesOnly?: boolean;
  onRequestClose: () => void;
  onRequestSubmit: (instrumentData: Array<InstrumentData>) => void | Promise<void>;
  attachAndProcessFiles?: (
    attachInstrumentData: Array<InstrumentData>,
    processInstrumentData: Array<InstrumentData>
  ) => void | Promise<void>;
}

export async function extractFiles(
  state: ImportInstrumentDataModalState,
  runId?: string
): Promise<Array<string>> {
  const instrumentData: Array<InstrumentData> = [];
  const instrumentQueryIds: Array<string> = [];
  const params: {[key: string]: string | boolean} = {};
  if (state.loginUsername) {
    params.username = state.loginUsername;
  }

  for (const index of state.selectedRowIndices.values()) {
    const instrumentDatum = state.instrumentData[index];
    const instrumentQueryId =
      instrumentDatum.status === 'UPLOADED'
        ? instrumentDatum.instrumentQueryId
        : (await createCopyObjectInstrumentQuery(instrumentDatum, params, runId)).instrumentQueryId;

    instrumentData.push(instrumentDatum);
    instrumentQueryIds.push(instrumentQueryId);
  }
  for (const instrumentQueryId of instrumentQueryIds) {
    await pollInstrumentQueryStatus(instrumentQueryId);
  }
  return instrumentQueryIds;
}

function getStepButtons(
  state: ImportInstrumentDataModalState,
  dispatch: Dispatch<ImportInstrumentDataModalAction>,
  onRequestSubmit: (instrumentData: Array<InstrumentData>) => void | Promise<void>,
  automationOutputFileConfig: AutomationOutputFileConfig | ClientAutomationOutputFileConfigSpec,
  runId?: string,
  attachAndProcessFiles?: (
    attachInstrumentData: Array<InstrumentData>,
    processInstrumentData: Array<InstrumentData>
  ) => void | Promise<void>,
  isFileSyncingAdapter?: boolean
): Array<ModalButton> {
  if (state.currentStepKey === StepKey.LOGIN) {
    return [
      {
        text: 'Next',
        disabled: state.loginQueryStatus !== InstrumentQueryStatus.DONE,
        submit: true,
        onClick: () => {
          dispatch({
            type: ImportInstrumentDataModalActionType.UPDATE_CURRENT_STEP,
            nextStepKey: StepKey.PROCESS,
          });
        },
      },
    ];
  } else if (state.currentStepKey === StepKey.ATTACH) {
    return [
      {
        // Can skip attaching if at least one file has been selected for processing.
        text: state.isImporting
          ? 'Processing...'
          : state.selectedRowIndices.size === 0 && state.selectedProcessInstrumentData.length > 0
            ? 'Skip'
            : 'Done',
        // Can submit if at least one file has been selected for attaching or processing.
        disabled:
          state.isImporting ||
          state.selectedRowIndices.size + state.selectedProcessInstrumentData.length === 0,
        submit: true,
        onClick: async () => {
          dispatch({
            type: ImportInstrumentDataModalActionType.IS_IMPORTING,
            isImporting: true,
          });
          try {
            logProfilerTimingEvent(LAB_AUTOMATION_CREATE_CONNECT_FILE_STARTED_EVENT);
            // NOTE: this check is just to make type checks happy. We will never enter the ATTACH step when
            // attachAndProcessFiles is undefinded, because that is the condition for entering the step.
            if (attachAndProcessFiles) {
              await attachAndProcessFiles(
                [...state.selectedRowIndices].map((index: number) => state.instrumentData[index]),
                state.selectedProcessInstrumentData
              );
            }
            // Call onRequestSubmit just to close the modal.
            await onRequestSubmit([]);
            logProfilerTimingEvent(LAB_AUTOMATION_CREATE_CONNECT_FILE_ENDED_EVENT);
          } finally {
            dispatch({
              type: ImportInstrumentDataModalActionType.IS_IMPORTING,
              isImporting: false,
            });
          }
        },
      },
    ];
  }
  return [
    {
      text: state.isImporting
        ? 'Processing...'
        : attachAndProcessFiles
          ? state.selectedRowIndices.size === 0
            ? 'Skip'
            : 'Next'
          : 'Process',
      disabled: state.isImporting || (state.selectedRowIndices.size === 0 && !attachAndProcessFiles),
      submit: true,
      onClick: async () => {
        dispatch({
          type: ImportInstrumentDataModalActionType.IS_IMPORTING,
          isImporting: true,
        });
        try {
          let instrumentData = state.instrumentData;
          let selectedRowIndices = state.selectedRowIndices;
          // If this is not file syncing adatper, we have not retrieved files yet, so do that now.
          if (!isFileSyncingAdapter) {
            logProfilerTimingEvent(LAB_AUTOMATION_RETRIEVE_CONNECT_FILE_STARTED_EVENT);
            const instrumentQueryIds = await extractFiles(state, runId);
            logProfilerTimingEvent(LAB_AUTOMATION_RETRIEVE_CONNECT_FILE_ENDED_EVENT);
            dispatch({
              type: ImportInstrumentDataModalActionType.SET_INSTRUMENT_QUERY_FILTER,
              instrumentQueryIds,
            });
            // NOTE: this should be impossible - it is already checked before entering the modal.
            if (!automationOutputFileConfig.transformDataType) {
              notifications.error(MISSING_TRANSFORM_TYPE_ERROR);
              return;
            }
            // Then, retrieve instrument data for transform file, and set that to selection for processing.
            await searchInstrumentData({
              searchFilter: '',
              connectionFilter: [],
              connectionSchemaFilter: [],
              nextToken: '',
              fileTypeFilter: InstrumentDataFileType.TRANSFORM,
              instrumentQueryFilter: instrumentQueryIds,
              transformDataTypeFilter: automationOutputFileConfig.transformDataType,
            }).then((searchResult) => {
              instrumentData = searchResult.instrumentData;
              selectedRowIndices = new Set<number>([...Array(searchResult.instrumentData.length).keys()]);
            });
          }
          if (attachAndProcessFiles) {
            // If attaching files, store files to process and wait for next step.
            dispatch({
              type: ImportInstrumentDataModalActionType.SET_PROCESS_INSTRUMENT_DATA,
              instrumentData: [...selectedRowIndices].map((index: number) => instrumentData[index]),
            });
          } else {
            // If not attaching files, call onRequestSubmit to process files and exit modal.
            logProfilerTimingEvent(LAB_AUTOMATION_CREATE_CONNECT_FILE_STARTED_EVENT);
            await onRequestSubmit([...selectedRowIndices].map((index: number) => instrumentData[index]));
            logProfilerTimingEvent(LAB_AUTOMATION_CREATE_CONNECT_FILE_ENDED_EVENT);
          }
        } finally {
          dispatch({
            type: ImportInstrumentDataModalActionType.UPDATE_CURRENT_STEP,
            nextStepKey: StepKey.ATTACH,
          });
        }
      },
    },
  ];
}

function getHelpText(
  currentStepKey: StepKey,
  instrumentServiceDetails: InstrumentServiceDetails | null,
  connectionId?: string
): string {
  if (currentStepKey === StepKey.PROCESS) {
    if (connectionId && instrumentServiceDetails && instrumentServiceDetails?.id != null) {
      return `Select which files to upload to your run for processing. Connection last updated on ${DateUtil.formalTime(
        instrumentServiceDetails.status.timestamp
      )}.`;
    } else {
      return 'Select which files to upload to your run for processing.';
    }
  } else if (currentStepKey === StepKey.LOGIN) {
    return 'Login to access your files.';
  } else if (currentStepKey === StepKey.ATTACH) {
    return 'Select files to attach directly to your run without processing.';
  } else {
    return '';
  }
}

function getStepsList(
  instrumentServiceDetails: InstrumentServiceDetails | null,
  hasAttachCallback?: boolean
): Array<{key: StepKey; title: string}> {
  const stepsList: Array<{key: StepKey; title: string}> = [];
  const requiresLogin = instrumentServiceDetails?.fields.per_user_login_required?.value === 'true';

  if (requiresLogin) {
    stepsList.push({
      key: StepKey.LOGIN,
      title: 'Login',
    });
  }
  stepsList.push({
    key: StepKey.PROCESS,
    title: 'Process files',
  });
  if (hasAttachCallback) {
    stepsList.push({
      key: StepKey.ATTACH,
      title: 'Attach files',
    });
  }
  return stepsList;
}

function endsWithAny(path: string, suffixes: Array<string>): boolean {
  return suffixes.some((suffix) => path.endsWith(suffix));
}

export default function ImportInstrumentDataModal(props: ImportInstrumentDataModalProps): JSX.Element {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  const defaultColumns = ['File name', 'Status', 'Modified at', 'Refetch'];
  const defaultWithConnectionColumns = ['File name', 'Status', 'Connection', 'Modified at'];
  const defaultProcessStepColumns = ['File name', 'Status', 'Data type'];

  const sortedSelectedRowIndices = [...state.selectedRowIndices].sort((a, b) => a - b);

  const [instrumentServiceDetails, setInstrumentServiceDetails] = useState(
    null as InstrumentServiceDetails | null
  );

  const isFileSyncingAdapter = !instrumentServiceDetails || instrumentServiceDetails.isFileSyncing;

  const buttons = getStepButtons(
    state,
    dispatch,
    props.onRequestSubmit,
    props.automationOutputFileConfig,
    props.runId,
    props.attachAndProcessFiles,
    isFileSyncingAdapter
  );
  const steps = useMemo(
    () => getStepsList(instrumentServiceDetails, Boolean(props.attachAndProcessFiles)),
    [instrumentServiceDetails, props.attachAndProcessFiles]
  );

  let initialFilterValues = {};
  if (props.connectionId) {
    initialFilterValues = {[FilterKey.CONNECTION]: [props.connectionId]};
  } else if (props.connectionSchemaId) {
    initialFilterValues = {[FilterKey.SCHEMA]: [props.connectionSchemaId]};
  }

  useEffect((): void => {
    setInstrumentServiceDetails(null);
    if (props.connectionId) {
      getInstrumentServiceDetails(props.connectionId).then((instrumentServiceDetails) => {
        setInstrumentServiceDetails(instrumentServiceDetails);
        const requiresLogin = instrumentServiceDetails.fields.per_user_login_required?.value === 'true';
        if (!requiresLogin) {
          dispatch({
            type: ImportInstrumentDataModalActionType.UPDATE_CURRENT_STEP,
            nextStepKey: StepKey.PROCESS,
          });
        }
      });
    } else {
      dispatch({
        type: ImportInstrumentDataModalActionType.UPDATE_CURRENT_STEP,
        nextStepKey: StepKey.PROCESS,
      });
    }
  }, [props.connectionId]);

  const setInstrumentDataCallback = useCallback(
    (instrumentData: Array<InstrumentData>): void => {
      dispatch({type: ImportInstrumentDataModalActionType.SET_INSTRUMENT_DATA, instrumentData});
    },
    [dispatch]
  );

  const isConnectionAvailable = instrumentServiceDetails?.status.value === ConnectionStatus.AVAILABLE;

  if (props.connectionId && !instrumentServiceDetails) {
    return (
      <Modal
        closeableOnEscapeKey={false}
        large={true}
        onRequestClose={props.onRequestClose}
        title="Retrieve data"
      >
        <CenteredHorizontalThrobber />
      </Modal>
    );
  }
  if (props.shouldShowUploadedFilesOnly) {
    state.instrumentData = state.instrumentData.filter(
      (instrumentData) => instrumentData.status === InstrumentFileStatus.UPLOADED
    );
  }
  return (
    <Modal
      buttons={buttons}
      closeableOnEscapeKey={false}
      large={true}
      onRequestClose={props.onRequestClose}
      title="Retrieve data"
    >
      <StepNav<StepKey>
        steps={steps}
        statusByStep={state.statusByStep}
        currentStepKey={state.currentStepKey}
        // NOTE: we don't use step nav to move between steps, instead using buttons, so this is unused.
        onUpdateCurrentStep={undefined}
        variant={StepNavVariant.FLAT}
      />
      {!BenchlingConfig.INSTRUMENTS_ENABLE_OFFLINE_CONNECTION_DEMOS &&
        instrumentServiceDetails &&
        !isConnectionAvailable && (
          <div className={css(styles.warningBanner)}>
            <InlineBanner
              bannerType={InlineBannerType.WARNING}
              text={
                instrumentServiceDetails.status.value === ConnectionStatus.DISABLED
                  ? 'This connection is disabled. Please re-enable it to retrieve the latest data.'
                  : 'This connection is not currently available. Please try again later for the latest data.'
              }
            />
          </div>
        )}
      <p className={css(styles.helpText)}>
        {getHelpText(state.currentStepKey, instrumentServiceDetails, props.connectionId)}
      </p>
      {state.currentStepKey === StepKey.LOGIN ? (
        <InstrumentLoginPage connectionId={props.connectionId!} dispatch={dispatch} state={state} />
      ) : (
        <InstrumentDataTable
          instrumentData={state.instrumentData}
          setInstrumentData={setInstrumentDataCallback}
          selectedRowIndices={state.selectedRowIndices}
          onSelectRow={(rowIndex: number): void => {
            if (
              !isUnselectableFileStatus(state.instrumentData[rowIndex].status, isConnectionAvailable) &&
              (props.allowMultiple ||
                state.selectedRowIndices.size === 0 ||
                state.selectedRowIndices.has(rowIndex))
            ) {
              dispatch({
                type: ImportInstrumentDataModalActionType.SELECT_ROW,
                selectedRowIndex: rowIndex,
              });
            }
          }}
          allowMultiple={props.allowMultiple}
          columns={
            state.currentStepKey === StepKey.ATTACH
              ? defaultProcessStepColumns
              : props.connectionId
                ? defaultColumns
                : defaultWithConnectionColumns
          }
          instrumentQueryFilter={state.instrumentQueryFilter}
          // Can attach any type of file. For processing, only show transform files.
          fileTypeFilter={
            state.currentStepKey === StepKey.ATTACH
              ? undefined
              : state.currentStepKey === StepKey.PROCESS && isFileSyncingAdapter
                ? InstrumentDataFileType.TRANSFORM
                : InstrumentDataFileType.ORIGINAL
          }
          transformDataTypeFilter={
            state.currentStepKey === StepKey.ATTACH
              ? null
              : props.automationOutputFileConfig.transformDataType
          }
          filterOptions={props.connectionId ? [] : [FilterKey.CONNECTION]}
          initialFilterValues={initialFilterValues}
          isImporting={state.isImporting}
          isConnectionAvailable={isConnectionAvailable}
          loginUsername={state.loginUsername}
          automationOutputFileConfig={props.automationOutputFileConfig}
        />
      )}
      {state.currentStepKey === StepKey.PROCESS && !state.isImporting && (
        <div className={css(styles.previewContainer)}>
          {sortedSelectedRowIndices.map((index) =>
            // Check if s3Key exists, because pending files may not have an s3Key
            state.instrumentData[index].s3Key &&
            endsWithAny(state.instrumentData[index].s3Key, ['.csv', '.txt']) ? (
              <CSVFilePreview key={index} url={state.instrumentData[index].downloadUrl} maxRows={5} />
            ) : (
              <p className="u-spacing-top faded text-center">
                Preview available only for .csv and .txt files.
              </p>
            )
          )}
        </div>
      )}
    </Modal>
  );
}

export const styles = StyleSheet.create({
  helpText: {
    'font-style': 'italic',
    padding: '10px 0 20px',
  },
  previewContainer: {
    overflowX: 'scroll',
  },
  warningBanner: {
    marginTop: '10px',
  },
});
